import { Box, Checkbox, Radio } from "@primer/react";
import { createContext, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import styles from "./DataGrid.module.css";
import Paging from "./Paging";
import Table from "./Table";
import Toolbar from "./toolbar/Toolbar";
import {
  dynamicSort,
  GetDataGridRowCell,
  handleFilter,
  handleSearchByDataField,
  IniColumns,
} from "./type/ColumnHelper";
import {
  ICondition,
  IDataGridColumn,
  IDataGridProps,
  ILogicOperator,
} from "./type/IDataGridProps";
import { useAutoTableHeight } from "./useAutoTableHeight";
export interface IDataGridContainerContext extends IDataGridProps {
  _pagedData: any[];
  _pageCount: number;
  _columns: IDataGridColumn[];
  _onColumnSettingChanged: (columns: IDataGridColumn[]) => void;
  _conditions: ICondition[];
  _setConditions: (conditions: ICondition[]) => void;
}

export const DataGridContainerContext = createContext(
  {} as IDataGridContainerContext,
);
const DataGrid = (props: IDataGridProps) => {
  const { height, dataSource } = props;

  const [logicOperator, setLogicOperator] = useState<ILogicOperator>("and");
  const [_columns, _setColumns] = useState<IDataGridColumn[]>([]);
  const [_paging, _setPaging] = useState(props.paging);
  const [_searchKeyDelayed, _setSearchKeyDelayed] = useState("");
  const [_sorting, _setSorting] = useState(props.sorting);
  const [_conditions, _setConditions] = useState<ICondition[]>([]);
  useEffect(() => {
    console.log({
      _conditions
    });

  }, [_conditions])
  const _pageSize = useMemo(() => {
    return _paging?.pageSize ?? 20;
  }, [_paging?.pageSize]);
  const _pageIndex = useMemo(() => {
    return _paging?.pageIndex ?? 0;
  }, [_paging?.pageIndex]);

  const dataColumn = useMemo(() => {
    return GetDataGridRowCell(_columns);
  }, [_columns]);
  const stickyOffsets = useMemo(() => {
    const leftOffsets: Record<string, number> = {};
    let left = 0;
    const rightOffsets: Record<string, number> = {};
    let right = 0;
    dataColumn.forEach((col) => {
      if (col.fixed === "left") {
        leftOffsets[col.uuid ?? ""] = left;
        left += col.width ?? 100;
      }
    });

    [...dataColumn].reverse().forEach((col) => {
      if (col.fixed === "right") {
        rightOffsets[col.uuid ?? ""] = right;
        right += col.width ?? 100;
      }
    });

    return { leftOffsets, rightOffsets };
  }, [dataColumn]);
  const baseColumns = useMemo(() => {
    return IniColumns(props.columns);
  }, [props.columns]);
  const dataFields = useMemo(() => {
    const dataFields = baseColumns
      .filter((x) => x.dataField)
      .map((x) => x.dataField as string);
    return dataFields;
  }, [baseColumns]);
  const filterdData: any[] = useMemo(() => {
    let resultData: any[] = [...dataSource];

    if (_searchKeyDelayed) {
      resultData = handleSearchByDataField(
        dataFields ?? [],
        dataSource,
        _searchKeyDelayed,
      );
    }
    if (_sorting?.enable !== false && _sorting?.mode && _sorting.field) {
      resultData = resultData.sort(dynamicSort(_sorting.field, _sorting.mode));
    }
    if (_conditions.length > 0) {
      resultData = handleFilter(_conditions, resultData, logicOperator);
    }
    return resultData;
  }, [dataSource, _searchKeyDelayed, dataFields, _sorting, _conditions]);
  useEffect(() => {
    if (props.onDataSourceFilterd) {
      props.onDataSourceFilterd(filterdData)
    }
  }, [filterdData, props])
  const _pageCount = useMemo(() => {
    if (_pageSize <= 0) return filterdData.length;
    const c = Math.floor(filterdData.length / _pageSize);
    if (c * _pageSize < filterdData.length) return c + 1;
    return c;
  }, [filterdData.length, _pageSize]);

  const _pagedData = useMemo(() => {
    let result: any[] = [];
    if (_paging?.enable === true) {
      const data = filterdData.slice(
        _pageSize * _pageIndex,
        _pageSize * _pageIndex + _pageSize,
      );
      result = data;
    } else {
      result = filterdData;
    }
    return result;
  }, [filterdData, _pageIndex, _pageSize, _paging?.enable]);
  const filterdDataKeys = useMemo(() => {
    // debugger;
    return filterdData.map((x) => x[props.keyExpr ?? "id"]);
  }, [filterdData]);
  const selectedRowKeys = useMemo(() => {
    return props.selection?.selectedRowKeys ?? [];
  }, [props.selection]);
  const isSelectedAll = useMemo(() => {
    return (
      filterdDataKeys.find((x) => !selectedRowKeys.includes(x)) === undefined
    );
  }, [selectedRowKeys, filterdDataKeys, props.keyExpr]);
  const selectionColumn: IDataGridColumn | null = useMemo(() => {
    if (!props.selection) return null;
    return {
      id: "selection",
      width: 40,
      align: "center",
      caption: "#",
      fixed: "left",
      isVisible: true,
      uuid: uuidv4(),
      headerRender: () => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {props.selection?.mode === "multiple" && (
              <Checkbox
                checked={isSelectedAll}
                onChange={(e) => {
                  if (e.target.checked) {
                    props.selection?.onSelectionChanged(filterdDataKeys);
                  } else {
                    props.selection?.onSelectionChanged([]);
                  }
                }}
              />
            )}
          </Box>
        );
      },
      cellRender: (data: any) => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {props.selection?.mode === "multiple" && (
              <Checkbox
                checked={selectedRowKeys.includes(data[props.keyExpr ?? "id"])}
                onChange={(e) => {
                  if (e.target.checked) {
                    props.selection?.onSelectionChanged([
                      ...selectedRowKeys,
                      data[props.keyExpr ?? "id"],
                    ]);
                  } else {
                    props.selection?.onSelectionChanged(
                      selectedRowKeys.filter(
                        (x) => x !== data[props.keyExpr ?? "id"],
                      ),
                    );
                  }
                }}
              />
            )}
            {props.selection?.mode === "single" && (
              <Radio
                name={data[props.keyExpr ?? "id"]}
                value={data[props.keyExpr ?? "id"]}
                aria-label="Select row"
                checked={selectedRowKeys.includes(data[props.keyExpr ?? "id"])}
                onChange={(e) => {
                  if (e.target.checked) {
                    props.selection?.onSelectionChanged([
                      data[props.keyExpr ?? "id"],
                    ]);
                  } else {
                    props.selection?.onSelectionChanged([]);
                  }
                }}
              />
            )}
          </Box>
        );
      },
    };
  }, [props.keyExpr, isSelectedAll, selectedRowKeys, filterdDataKeys]);
  const idxColumn: IDataGridColumn | null = useMemo(() => {
    if (props.isShowRowIdx === false) return null;
    return {
      id: "col_idx",
      width: 40,
      align: "center",
      caption: "#",
      fixed: "left",
      isVisible: true,
      uuid: uuidv4(),
    };
  }, []);

  const finalColumns = useMemo(() => {
    let cols: IDataGridColumn[] = [];
    if (idxColumn) cols.push(idxColumn);
    if (selectionColumn) cols.push(selectionColumn);
    cols.push(...baseColumns);
    return cols;
  }, [baseColumns, selectionColumn, idxColumn]);

  useEffect(() => {
    _setColumns(finalColumns);
  }, [finalColumns]);
  const _onColumnSettingChanged = (columns: IDataGridColumn[]) => {
    _setColumns(columns);
  };

  const contextValue: IDataGridContainerContext = {
    ...props,
    _pagedData,
    _pageCount,
    _columns: _columns,
    _onColumnSettingChanged,
    _conditions,
    _setConditions,
  };

  const { containerRef, toolbarRef, tableRef, pagingRef, tableHeight } =
    useAutoTableHeight({ debounceMs: 100 });

  return (
    <DataGridContainerContext.Provider value={contextValue}>
      <Box
        sx={{
          height: height,
          overflow: "auto",
          // border: "1px solid #ccc",
        }}
        className={styles.container}
        ref={containerRef}
      >
        <Box ref={toolbarRef} sx={{ pb: 1 }}>
          <Toolbar
            onSearchKeyChanged={_setSearchKeyDelayed}
            filterdData={filterdData}
            dataColumns={dataColumn}
          />
          {props.subToolbarElement && <>{props.subToolbarElement}</>}
        </Box>
        <Box ref={tableRef}>
          <Table
            conditions={_conditions}
            dataSource={props.dataSource}
            dataColumn={dataColumn}
            stickyOffsets={stickyOffsets}
            height={props.height ? tableHeight : undefined}
            sorting={_sorting}
            onSortingChanged={(value) => {
              if (_sorting?.enable !== false) {
                _setSorting(value);
              }
            }}
            isLoading={props.isLoading}
            focusedCell={props.focusedCell}
            mainColor={"#1a7f37"}
            totalSummary={props.totalSummary}
            filterdData={filterdData}
          />
        </Box>
        <Box ref={pagingRef} sx={{ pt: 1 }}>
          {props.paging?.enable === true && _pageCount > 0 && (
            <Paging
              pageSizeItems={props.paging.pageSizeItems}
              pageCount={_pageCount}
              pageIndex={_pageIndex}
              pageSize={_pageSize}
              totalCount={filterdData.length}
              onPagingChanged={(pageIndex, pageSize) => {
                _setPaging({
                  ..._paging,
                  pageIndex: pageIndex,
                  pageSize: pageSize,
                } as any);
              }}
            />
          )}
        </Box>
      </Box>
    </DataGridContainerContext.Provider>
  );
};

export default DataGrid;
